import { lessonClassApi } from '@mathflat/domain/LessonClass'
import {
  type Sms as SmsData,
  smsApi,
  type SmsHistoryParams,
  type SmsTemplate as SmsTemplateData,
  type SmsTemplateBody,
  type SmsTemplateSampleParams,
  type SmsTemplateSendBody,
  type SmsTemplateSendingType,
} from '@mathflat/domain/Sms'
import {
  type Student as StudentData,
  studentApi,
  type StudentPhoneGroup,
} from '@mathflat/domain/Student'
import { formatISO } from 'date-fns/esm'
import ExtendedMap from 'src/models/ExtendedMap'

import type { SmsPost, Student } from '@/dto'
import {
  Sms,
  SmsCollection,
  SmsStudent,
  SmsStudentGroup,
  SmsStudentGroupColl,
  SmsSummary,
  SmsTemplate,
} from '@/dto'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

// 학년 묶음(StudentPhoneGroup)과 반 묶음이 같은 모양의 멤버 목록을 싣는다. 나머지 필드는 묶음마다 다르다.
type SmsStudentGroupSource = {
  memberList: (Pick<StudentPhoneGroup['memberList'][number], 'id' | 'name'> & {
    phone?: StudentPhoneGroup['memberList'][number]['phone']
    parent: { phone?: StudentPhoneGroup['memberList'][number]['parent']['phone'] }
  })[]
}

type SmsHistoryPageParams = Pick<SmsHistoryParams, 'page' | 'size' | 'sort'>

export class SmsController implements Controller {
  adapter = {
    sms: {
      from(data: SmsData): Sms {
        return new Sms(data)
      },
      to(
        studentId: StudentData['id'],
        recipient: SmsStudentGroupColl['recipient'],
      ): Pick<SmsHistoryParams, 'studentIdList' | 'target'> {
        const target = recipient === '학부모' ? 'PARENTS' : 'STUDENT'
        return { studentIdList: [studentId], target }
      },
      toDate(data: {
        startDatetime: Date
        endDatetime: Date
      }): Pick<SmsHistoryParams, 'startDatetime' | 'endDatetime'> {
        return {
          startDatetime: formatISO(data.startDatetime),
          endDatetime: formatISO(data.endDatetime),
        }
      },
    },
    template: makeAdapter({
      from(data: SmsTemplateData): SmsTemplate {
        return new SmsTemplate(data)
      },
      to(data: SmsTemplate): SmsTemplateBody {
        return { title: data.title, content: data.content }
      },
    }),
    studentGroup: makeAdapter({
      from(data: SmsStudentGroupSource) {
        const memberList = data.memberList.map((member) => new SmsStudent(member))
        return new SmsStudentGroup({
          ...data,
          memberList,
        })
      },
    }),
  }

  fetchSmsStudents = async () => {
    const [gradeStudentGroups, classStudentGroups] = await Promise.all([
      this.fetchGradeSmsStudents(),
      this.fetchClassSmsStudents(),
    ])
    return new SmsStudentGroupColl({ gradeStudentGroups, classStudentGroups })
  }

  private fetchGradeSmsStudents = async () => {
    const data = await studentApi.getStudentPhoneGroups({
      mp_event_name: 'T99-00_출결관리_학년별보기_특정학생출결정보조회_API',
    })
    return data.map(this.adapter.studentGroup.from)
  }

  private fetchClassSmsStudents = async () => {
    const { content } = await lessonClassApi.getLessonClassPhoneGroups(undefined, {
      mp_event_name: 'T52-02_문자전송_반별보기_학생번호조회_API',
    })

    return content.map(this.adapter.studentGroup.from)
  }

  fetchSmsById = async (
    id: StudentData['id'],
    recipient: SmsStudentGroupColl['recipient'],
    pageParams: SmsHistoryPageParams,
  ) => {
    const {
      page: { content, last },
    } = await smsApi.getSmsHistory(
      { ...pageParams, ...this.adapter.sms.to(id, recipient) },
      { mp_event_name: 'T52-05_문자발송결과조회_API' },
    )
    const smsArr = content.map(this.adapter.sms.from)
    return { smsColl: new SmsCollection(ExtendedMap.fromArr(smsArr, 'id')), last }
  }

  fetchSmsUsage = async (
    params: { startDatetime: Date; endDatetime: Date },
    pageParams: SmsHistoryPageParams,
  ) => {
    const {
      page: { content, last },
      summary,
    } = await smsApi.getSmsHistory(
      { ...pageParams, ...this.adapter.sms.toDate(params) },
      { mp_event_name: 'T52-05_문자발송결과조회_API' },
    )
    const smsArr = content.map(this.adapter.sms.from)
    return {
      collection: new SmsCollection(ExtendedMap.fromArr(smsArr, 'id')),
      summary: new SmsSummary(summary),
      last,
    }
  }

  fetchSmsTemplate = async (pageParams?: Parameters<typeof smsApi.getSmsTemplates>[0]) => {
    const { content } = await smsApi.getSmsTemplates(pageParams, {
      mp_event_name: 'T52-03_문자템플릿조회_API',
    })

    return content.map(this.adapter.template.from)
  }

  fetchSmsBasicTemplateBySendingType = async (templateSendingType: SmsTemplateSendingType) => {
    const template = await smsApi.getSmsBasicTemplate(templateSendingType, {
      mp_event_name: 'T52-03_문자템플릿조회_API',
    })

    return this.adapter.template.from(template)
  }

  deleteSmsTemplateById = async (id: SmsTemplate['id']) => {
    await smsApi.deleteSmsTemplates([id], { mp_event_name: 'T52-03_문자템플릿삭제_API' })
  }

  createSmsTemplate = async (template: SmsTemplate) => {
    await smsApi.createSmsTemplate(this.adapter.template.to(template), {
      mp_event_name: 'T52-05_문자발송결과조회_API',
    })
  }

  patchSmsTemplate = async (template: SmsTemplate) => {
    await smsApi.updateSmsTemplate(template.id, this.adapter.template.to(template), {
      mp_event_name: 'T52-03_문자템플릿수정_API',
    })
  }

  sendSms = async ({ sendingTarget, studentIdList, content }: SmsPost) => {
    await smsApi.sendSms(
      { sendingTarget, studentIdList, content },
      { mp_event_name: 'T99-00_문자발송_API' },
    )
  }

  fetchAttendanceSmsSample = async (params: SmsTemplateSampleParams) => {
    return smsApi.getSmsTemplateSampleMessage(params, {
      mp_event_name: 'T52-01_문자발송될샘플조회_API',
    })
  }

  sendIdGuideSmsToStudent = async (
    templateId: SmsTemplateSendBody['templateId'],
    studentId: Student['id'][],
  ) => {
    await smsApi.sendSmsByTemplate(
      { templateId, sendingTarget: 'STUDENT', studentIdList: studentId },
      { mp_event_name: 'T52-01_문자템플릿기반문자발송_API' },
    )
  }

  sendIdGuideSmsToParents = async (
    templateId: SmsTemplateSendBody['templateId'],
    studentId: Student['id'][],
  ) => {
    await smsApi.sendSmsByTemplate(
      { templateId, sendingTarget: 'PARENTS', studentIdList: studentId },
      { mp_event_name: 'T52-01_문자템플릿기반문자발송_API' },
    )
  }
}

export const smsController = new SmsController()
